import React, { useState, useEffect, useCallback } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { motion, AnimatePresence } from 'framer-motion';
import Mixpanel from '../utils/mixpanel';
import { PageSEO } from '../components/SEO/PageSEO';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

const calculatorPageFaqs = [
  {
    question: "How accurate is the event budget calculator?",
    answer: "Our calculator is based on real NYC venue pricing data and hundreds of actual bookings. Estimates typically fall within 10-15% of final quotes, helping you plan for corporate dinners (25-50 guests), premium cocktail parties, and full restaurant buyouts (50-250 guests)."
  },
  {
    question: "What costs does the calculator include?",
    answer: "It estimates food and beverage minimums, open bar costs, venue fees, gratuity, and NYC sales tax. It focuses on groups of 25+ to provide realistic spend projections for premium events."
  }
];

const FAQItem = ({ faq, isOpen, toggle }) => (
  <div className="border-b border-stone-200 py-4">
    <button
      onClick={toggle}
      className="w-full flex justify-between items-center text-left focus:outline-none"
    >
      <span className="text-lg font-semibold text-gray-900 leading-tight pr-4" style={{ fontFamily: "'Playfair Display', serif" }}>
        {faq.question}
      </span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-stone-400 flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-stone-400 flex-shrink-0" />
      )}
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <p className="mt-3 text-stone-600 leading-relaxed text-sm">
            {faq.answer}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// Define group size tiers and their base multipliers
const GROUP_TIERS = {
  tier1: { max: 10, multiplier: 1.2, name: '10' },
  tier2: { max: 20, multiplier: 1.1, name: '20' },
  tier3: { max: 30, multiplier: 1.0, name: '30' },
  tier4: { max: 40, multiplier: 0.95, name: '40' },
  tier5: { max: 50, multiplier: 0.9, name: '50' },
  tier6: { max: 75, multiplier: 0.85, name: '75' },
  tier7: { max: 100, multiplier: 0.8, name: '100' },
  tier8: { max: 150, multiplier: 0.75, name: '150' },
  tier9: { max: 200, multiplier: 0.7, name: '200' },
  tier10: { max: 500, multiplier: 0.65, name: '500' }
};

// Define the exact pricing matrix from the table
const PRICING_MATRIX = {
  '10': { 2: 350, 3: 450, 4: 500, 5: 550, 6: 600, 7: 650, 8: 700 },
  '20': { 2: 1000, 3: 1250, 4: 1350, 5: 1500, 6: 1650, 7: 1750, 8: 1900 },
  '30': { 2: 1750, 3: 2200, 4: 2375, 5: 2625, 6: 2900, 7: 3075, 8: 3325 },
  '40': { 2: 2250, 3: 2800, 4: 3025, 5: 3350, 6: 3700, 7: 3925, 8: 4250 },
  '50': { 2: 2800, 3: 3500, 4: 3780, 5: 4200, 6: 4620, 7: 4900, 8: 5320 },
  '75': { 2: 3850, 3: 4800, 4: 5200, 5: 5775, 6: 6350, 7: 6750, 8: 7325 },
  '100': { 2: 5250, 3: 6550, 4: 7100, 5: 7875, 6: 8650, 7: 9200, 8: 9975 },
  '150': { 2: 7000, 3: 8750, 4: 9450, 5: 10500, 6: 11550, 7: 12250, 8: 13300 },
  '200': { 2: 9450, 3: 11800, 4: 12750, 5: 14175, 6: 15600, 7: 16550, 8: 17975 },
  '500': { 2: 12600, 3: 15750, 4: 17000, 5: 18900, 6: 20800, 7: 22050, 8: 23950 }
};

// Define duration options consistently
const DURATION_OPTIONS = ["2 hours", "3 hours", "4 hours", "5 hours", "6 hours", "7 hours", "8 hours"];

// New simplified multipliers
const MULTIPLIERS = {
  venueType: { budget: 0.57, casual: 0.85, upscale: 1.0, luxury: 1.25 },
  fbMinimum: { budget: 0.35, casual: 0.75, upscale: 0.95, luxury: 1.15 },
  eventDay: { weekday: 0.95, weekend: 1.20 },
  location: { manhattan: 1.05, brooklyn: 0.95, queens: 0.85 },
  foodTier: { none: 0, appetizers: 24, buffet: 43, plated: 62 },
  barPackage: { noBar: 0, limited: 19, standard: 28.5, premium: 38 }
};

const VENUE_ONLY_PRICING = {
  '10': { 2: 300, 3: 400, 4: 450, 5: 500, 6: 550, 7: 600, 8: 650 },
  '20': { 2: 900, 3: 1100, 4: 1300, 5: 1500, 6: 1700, 7: 1900, 8: 2100 },
  '30': { 2: 1500, 3: 1900, 4: 2200, 5: 2500, 6: 2800, 7: 3100, 8: 3400 },
  '40': { 2: 1800, 3: 2200, 4: 2600, 5: 3000, 6: 3400, 7: 3800, 8: 4200 },
  '50': { 2: 2400, 3: 2900, 4: 3400, 5: 3400, 6: 3900, 7: 4400, 8: 4900 },
  '75': { 2: 3200, 3: 3900, 4: 4600, 5: 5300, 6: 6000, 7: 6700, 8: 7400 },
  '100': { 2: 4000, 3: 5000, 4: 6000, 5: 6500, 6: 7900, 7: 8500, 8: 9500 },
  '150': { 2: 4800, 3: 5800, 4: 5300, 5: 6300, 6: 7300, 7: 8300, 8: 9300 },
  '200': { 2: 7200, 3: 9000, 4: 10800, 5: 12600, 6: 14400, 7: 13700, 8: 16200 },
  '500': { 2: 12000, 3: 15000, 4: 18000, 5: 21000, 6: 24000, 7: 27000, 8: 28500 }
};

const VENUE_FEE_TIERS = {
  tier1: { max: 5000, rate: 0.05 },
  tier2: { max: 10000, rate: 0.045 },
  tier3: { max: 15000, rate: 0.04 },
  tier4: { max: 20000, rate: 0.035 },
  tier5: { max: Infinity, rate: 0.03 }
};

const getGuestRangeKey = (guestCount) => {
  const count = Number(guestCount);
  if (isNaN(count) || count < 1) return null;
  if (count <= 10) return '10';
  if (count <= 20) return '20';
  if (count <= 30) return '30';
  if (count <= 40) return '40';
  if (count <= 50) return '50';
  if (count <= 75) return '75';
  if (count <= 100) return '100';
  if (count <= 150) return '150';
  if (count <= 200) return '200';
  return '500';
};

const VenueStyleSelector = ({ formData, handleInputChange }) => (
  <div className="p-1 relative z-10">
    <div className="text-gray-700 font-medium mb-3">Venue Style</div>
    <div className="grid grid-cols-4 gap-3">
      {[
        { name: 'Budget', value: 'budget', icon: '🏡' },
        { name: 'Casual', value: 'casual', icon: '🏠' },
        { name: 'Upscale', value: 'upscale', icon: '✨' },
        { name: 'Luxury', value: 'luxury', icon: '💎' }
      ].map((style) => (
        <motion.div 
          key={style.value}
          onClick={() => handleInputChange('venueType', style.value)}
          className={`relative p-4 rounded-xl cursor-pointer transition-all duration-300
            ${formData.venueType === style.value 
              ? 'bg-white border border-stone-300 shadow-md' 
              : 'bg-white/80 border border-stone-100 hover:border-stone-200'}`}
          whileHover={{ y: -5, scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex flex-col items-center text-center">
            <span className="text-xl mb-2">{style.icon}</span>
            <div className={`text-sm ${formData.venueType === style.value ? 'font-medium text-stone-900' : 'text-gray-800'}`}>
              {style.name}
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

const DURATION_SCALING = { 2: 1.0, 3: 0.95, 4: 0.90, 5: 0.85, 6: 0.80, 7: 0.75, 8: 0.70 };

const BAR_SCALING = { 10: 1.0, 20: 0.98, 30: 0.97, 40: 0.96, 50: 0.95, 75: 0.94, 100: 0.93, 150: 0.92, 200: 0.91, 500: 0.90 };

const GUEST_COUNT_SCALING = { 10: 1.2, 20: 1.1, 30: 1.0, 40: 0.95, 50: 0.9, 75: 0.85, 100: 0.8, 150: 0.75, 200: 0.7, 500: 0.65 };

const ModernSwitch = ({ checked, onChange, label }) => (
  <div className="flex items-center gap-3">
    <motion.div 
      className={`relative w-14 h-7 rounded-full cursor-pointer ${checked ? 'bg-stone-800' : 'bg-stone-200'}`}
      onClick={() => onChange(!checked)}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <motion.div 
        className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-sm"
        initial={false}
        animate={{ left: checked ? "auto" : "1px", right: checked ? "1px" : "auto" }}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
      />
    </motion.div>
    <span className={`font-medium ${checked ? 'text-stone-900' : 'text-gray-700'}`}>
      {label}
    </span>
  </div>
);

const roundTo25 = (num) => Math.ceil(num / 25) * 25;

const getSavingsText = (savings) => {
  if (!savings || savings < 100) return null;
  if (savings >= 5000) return '$$$';
  if (savings >= 1000) return '$$';
  return '$';
};

const calculateSavings = (currentCost, savingsPercentage) => currentCost * savingsPercentage;

const getSmartSuggestion = (formData, costs) => {
  if (!formData.numGuests || !formData.duration || !formData.eventDay || !formData.location) return [];

  const suggestions = [];
  
  if (formData.eventDay === 'weekend') {
    const savings = calculateSavings(costs.total, 0.35);
    const savingsText = getSavingsText(savings);
    if (savingsText) suggestions.push({ type: 'scheduling', icon: '📅', text: `Save ${savingsText} by switching to a weekday`, action: { label: 'Switch to Weekday', changes: { eventDay: 'weekday' } } });
  }

  if (formData.location === 'manhattan') {
    const savings = calculateSavings(costs.total, 0.3);
    const savingsText = getSavingsText(savings);
    if (savingsText) suggestions.push({ type: 'location', icon: '📍', text: `Save ${savingsText} by switching to Brooklyn`, action: { label: 'Switch to Brooklyn', changes: { location: 'brooklyn' } } });
  }

  if (formData.venueType === 'luxury') {
    const savings = calculateSavings(costs.total, 0.45);
    const savingsText = getSavingsText(savings);
    if (savingsText) suggestions.push({ type: 'venue', icon: '✨', text: `Save ${savingsText} by choosing an upscale venue`, action: { label: 'Switch to Upscale', changes: { venueType: 'upscale' } } });
  }

  if (formData.venueType === 'upscale' || formData.venueType === 'luxury') {
    const savings = calculateSavings(costs.total, 0.35);
    const savingsText = getSavingsText(savings);
    if (savingsText) suggestions.push({ type: 'venue', icon: '🏠', text: `Save ${savingsText} by choosing a casual venue`, action: { label: 'Switch to Casual', changes: { venueType: 'casual' } } });
  }

  if (parseInt(formData.duration) >= 5) {
    const hourlyRate = (costs.total / parseInt(formData.duration));
    const savingsText = getSavingsText(hourlyRate);
    if (savingsText) suggestions.push({ type: 'duration', icon: '⏰', text: `Save ${savingsText} by reducing duration by 1 hour`, action: { label: 'Reduce Duration', changes: { duration: (parseInt(formData.duration) - 1).toString() } } });
  }

  if (!formData.venueRentalOnly) {
    if (formData.barPricingType === 'openBar') {
      const savings = calculateSavings(costs.barCost, 0.3);
      const savingsText = getSavingsText(savings);
      if (savingsText) suggestions.push({ type: 'service', icon: '🍷', text: `Save ${savingsText} by switching to bar minimum`, action: { label: 'Switch to Bar Minimum', changes: { barPricingType: 'barMinimum' } } });

      if (formData.liquorQuality === 'premium') {
        const savings = calculateSavings(costs.barCost, 0.4);
        const savingsText = getSavingsText(savings);
        if (savingsText) suggestions.push({ type: 'bar', icon: '🥂', text: `Save ${savingsText} by switching to standard bar package`, action: { label: 'Switch to Standard', changes: { liquorQuality: 'standard' } } });
      }

      if (formData.liquorQuality === 'standard' || formData.liquorQuality === 'premium') {
        const savings = calculateSavings(costs.barCost, 0.3);
        const savingsText = getSavingsText(savings);
        if (savingsText) suggestions.push({ type: 'bar', icon: '🍺', text: `Save ${savingsText} by switching to limited bar package`, action: { label: 'Switch to Limited', changes: { liquorQuality: 'limited' } } });
      }
    }

    if (formData.foodTier === 'plated') {
      const savings = calculateSavings(costs.foodCost, 0.3);
      const savingsText = getSavingsText(savings);
      if (savingsText) suggestions.push({ type: 'food', icon: '🍽️', text: `Save ${savingsText} by switching to buffet service`, action: { label: 'Switch to Buffet', changes: { foodTier: 'buffet' } } });
    }

    if (formData.foodTier === 'buffet' || formData.foodTier === 'plated') {
      const savings = calculateSavings(costs.foodCost, 0.4);
      const savingsText = getSavingsText(savings);
      if (savingsText) suggestions.push({ type: 'food', icon: '🥗', text: `Save ${savingsText} by switching to appetizers only`, action: { label: 'Switch to Appetizers', changes: { foodTier: 'appetizers' } } });
    }
  }

  if (!formData.venueRentalOnly && costs.foodCost + costs.barCost > 3000) {
    const savings = calculateSavings(costs.foodCost + costs.barCost, 0.4);
    const savingsText = getSavingsText(savings);
    if (savingsText) suggestions.push({ type: 'service', icon: '🏢', text: `Save ${savingsText} by switching to venue rental only`, action: { label: 'Switch to Venue Only', changes: { venueRentalOnly: true } } });
  }

  return suggestions;
};

const SmartSuggestions = ({ formData, costs, handleInputChange }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestions = getSmartSuggestion(formData, costs);
  
  if (suggestions.length === 0) return null;

  return (
    <div className="mt-6">
      <motion.button 
        onClick={() => setShowSuggestions(!showSuggestions)}
        className="w-full flex items-center justify-between p-3 text-sm font-medium bg-[#FAFAF8] rounded-xl border border-stone-200 hover:border-stone-300"
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <span className="flex items-center gap-2">
          <span className="bg-stone-800 text-white p-1 rounded-lg text-sm">💡</span>
          <span className="text-stone-800 font-medium">Money-Saving Suggestions ({suggestions.length})</span>
        </span>
        <motion.span animate={{ rotate: showSuggestions ? 180 : 0 }} transition={{ duration: 0.3 }} className="text-stone-500">⌄</motion.span>
      </motion.button>

      <AnimatePresence>
        {showSuggestions && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="mt-3 space-y-2 overflow-hidden">
            {suggestions.map((suggestion, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group p-4 bg-white rounded-xl border border-stone-100 hover:border-stone-200 hover:shadow-sm transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-stone-100 text-xl">{suggestion.icon}</div>
                  <div className="flex-1">
                    <p className="text-gray-700">{suggestion.text}</p>
                    <motion.button
                      onClick={() => Object.entries(suggestion.action.changes).forEach(([field, value]) => handleInputChange(field, value))}
                      className="mt-3 text-xs font-medium text-white bg-stone-800 rounded-full px-4 py-1.5 flex items-center gap-1 hover:bg-stone-700"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>{suggestion.action.label}</span><span>→</span>
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const hasFormData = (data) => Object.values(data).some(value => value !== '' && value !== false && value !== 'none' && value !== 'barMinimum' && value !== 'upscale');

// FAQ Schema for Calculator page
const calculatorPageFaqSchema = [
  {
    question: "How accurate is the LetsHost event budget calculator?",
    answer: "Our calculator is based on real NYC venue pricing data and hundreds of actual bookings. Estimates typically fall within 10-15% of final quotes. Actual costs vary based on specific venue selection, negotiation, and exact package details."
  },
  {
    question: "What costs does the calculator include?",
    answer: "The calculator estimates food and beverage minimums or open bar costs, venue service fees, gratuity (typically 20%), and NYC sales tax (8.875%). It does not include optional services like AV equipment, decor, or entertainment."
  },
  {
    question: "What's the difference between bar minimum and open bar?",
    answer: "Bar minimum requires a set food and beverage spend—you pay for what you order. Open bar charges a per-person hourly rate for unlimited drinks. Open bar typically costs $19-$38 per person per hour depending on liquor quality, while bar minimums range from $1,500-$15,000+ based on group size and venue."
  },
  {
    question: "Why do weekday events cost less than weekend events?",
    answer: "Most venues offer 15-25% lower pricing on weekdays due to lower demand. Monday through Thursday corporate events are often the most cost-effective option for groups with flexible scheduling."
  },
  {
    question: "How much should I budget per person for a corporate dinner in NYC?",
    answer: "For a sit-down corporate dinner with open bar, budget $125-$250 per person before tax and gratuity. Cocktail party events typically run $75-$150 per person. Full buyouts vary widely from $5,000 to $30,000+ depending on venue and capacity."
  }
];

export default function Calculator() {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [costs, setCosts] = useState({
    baseCost: 0, foodCost: 0, barCost: 0, adminFee: 0, gratuity: 0, tax: 0, total: 0, subtotal: 0
  });

  const [formData, setFormData] = useState({
    venueRentalOnly: false, barPricingType: 'barMinimum', location: '', numGuests: '', duration: '',
    foodTier: 'none', liquorQuality: '', venueType: 'casual', eventDay: ''
  });

  const [hasInteracted, setHasInteracted] = useState(false);
  const [previousServiceSelections, setPreviousServiceSelections] = useState({ barPricingType: '', liquorQuality: '', foodTier: '' });

  const handleCalculatorInteraction = () => { if (!hasInteracted) setHasInteracted(true); };

  const handleClear = () => {
    setFormData({ venueRentalOnly: false, barPricingType: 'barMinimum', location: '', numGuests: '', duration: '', foodTier: 'none', liquorQuality: '', venueType: 'casual', eventDay: '' });
    setCosts({ baseCost: 0, foodCost: 0, barCost: 0, adminFee: 0, gratuity: 0, tax: 0, total: 0, subtotal: 0 });
    localStorage.removeItem('calculatorFormData');
    localStorage.removeItem('calculatorCosts');
    Mixpanel.track('Calculator Reset');
    setHasInteracted(true);
  };

  const calculateCost = useCallback((currentFormData) => {
    const { numGuests, duration, eventDay, location, barPricingType, liquorQuality, foodTier, venueType, venueRentalOnly } = currentFormData;

    if (!numGuests || !duration || !eventDay || !location) return {};

    const durationHours = parseInt(duration.match(/(\d+)/)[1]);
    const guestRange = getGuestRangeKey(numGuests);
    let adjustedVenueCost = 0, barCost = 0, foodCost = 0;
    const dayMultiplier = MULTIPLIERS.eventDay[eventDay] || 1.0;
    const locationMultiplier = MULTIPLIERS.location[location] || 1.0;

    if (venueRentalOnly) {
      adjustedVenueCost = VENUE_ONLY_PRICING[guestRange][durationHours];
      adjustedVenueCost *= MULTIPLIERS.venueType[venueType] || 1.0;
      adjustedVenueCost *= dayMultiplier;
      adjustedVenueCost *= locationMultiplier;
      barCost = 0; foodCost = 0;
      const tax = adjustedVenueCost * 0.08875;
      return { baseCost: adjustedVenueCost, foodCost: 0, barCost: 0, adminFee: 0, gratuity: 0, tax, total: adjustedVenueCost + tax };
    } else {
      if (barPricingType === 'barMinimum') {
        barCost = PRICING_MATRIX[guestRange][durationHours];
        barCost *= MULTIPLIERS.fbMinimum[venueType] || 1.0;
        barCost *= dayMultiplier;
        barCost *= locationMultiplier;
      } else if (barPricingType === 'openBar' && liquorQuality) {
        const barCostPerPersonPerHour = MULTIPLIERS.barPackage[liquorQuality] || 0;
        const barScaling = BAR_SCALING[guestRange] || 1.0;
        barCost = barCostPerPersonPerHour * Number(numGuests) * durationHours * barScaling;
        barCost *= MULTIPLIERS.venueType[venueType] || 1.0;
        barCost *= dayMultiplier;
        barCost *= locationMultiplier;
        if (foodTier && foodTier !== 'none') {
          const foodCostPerPerson = MULTIPLIERS.foodTier[foodTier] || 0;
          foodCost = foodCostPerPerson * Number(numGuests);
          foodCost *= MULTIPLIERS.venueType[venueType] || 1.0;
          foodCost *= locationMultiplier;
        }
      }
      const totalBaseCost = barCost + foodCost;
      const venueFeeRate = calculateVenueFeeRate(totalBaseCost);
      const adminFee = totalBaseCost * venueFeeRate;
      const gratuity = foodCost * 0.20;
      const taxableAmount = totalBaseCost + adminFee + gratuity;
      const tax = taxableAmount * 0.08875;
      return { baseCost: 0, foodCost, barCost, adminFee, gratuity, tax, total: taxableAmount + tax };
    }
  }, []);

  const handleInputChange = (field, value) => {
    if (!hasInteracted) setHasInteracted(true);
    
    if (field === 'venueRentalOnly') {
      if (value === true) {
        setPreviousServiceSelections({ barPricingType: formData.barPricingType, liquorQuality: formData.liquorQuality, foodTier: formData.foodTier });
        setFormData(prev => ({ ...prev, venueRentalOnly: value, barPricingType: 'noBar', liquorQuality: 'none', foodTier: 'none' }));
        Mixpanel.track('Calculator Option Changed', { field: 'venueRentalOnly', value: 'true', action: 'Switched to venue rental only' });
      } else {
        setFormData(prev => ({ ...prev, venueRentalOnly: value, barPricingType: previousServiceSelections.barPricingType || 'barMinimum', liquorQuality: previousServiceSelections.liquorQuality || 'standard', foodTier: previousServiceSelections.foodTier || 'appetizers' }));
        Mixpanel.track('Calculator Option Changed', { field: 'venueRentalOnly', value: 'false', action: 'Switched to food & beverage service' });
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
      Mixpanel.track('Calculator Option Changed', { field, value: value.toString(), previousValue: formData[field] ? formData[field].toString() : 'none' });
    }
  };

  useEffect(() => {
    if (isValidInput(formData)) {
      const newCosts = calculateCost(formData);
      setCosts(newCosts);
      if (newCosts.total > 0) {
        Mixpanel.track('Calculator Cost Calculated', {
          total: Math.round(newCosts.total), guestCount: formData.numGuests, duration: formData.duration,
          venueType: formData.venueType, location: formData.location, eventDay: formData.eventDay, venueRentalOnly: formData.venueRentalOnly
        });
      }
    }
  }, [formData, calculateCost]);

  const isValidInput = (formData) => Boolean(formData.barPricingType && formData.eventDay && formData.location && formData.numGuests && formData.foodTier);

  const calculateVenueFeeRate = (baseTotal) => {
    for (const tier of Object.values(VENUE_FEE_TIERS)) { if (baseTotal <= tier.max) return tier.rate; }
    return VENUE_FEE_TIERS.tier5.rate;
  };

  const generateChartData = (costs) => {
    const roundedCosts = {
      baseCost: roundTo25(costs.baseCost || 0), barCost: roundTo25(costs.barCost || 0), foodCost: roundTo25(costs.foodCost || 0),
      adminFee: roundTo25(costs.adminFee || 0), gratuity: roundTo25(costs.gratuity || 0), tax: roundTo25(costs.tax || 0), total: roundTo25(costs.total || 0)
    };
    const total = roundedCosts.total || 1;
    const data = [
      formData.venueRentalOnly ? { name: 'Venue Rental', value: roundedCosts.baseCost } : { name: 'Food & Beverage', value: roundedCosts.barCost + roundedCosts.foodCost },
      { name: 'Venue Fee', value: roundedCosts.adminFee },
      { name: 'Gratuity', value: roundedCosts.gratuity },
      { name: 'Taxes', value: roundedCosts.tax }
    ].filter(item => item.value > 0);
    return data.map(item => ({ ...item, percentage: ((item.value / total) * 100).toFixed(1) }));
  };

  const CHART_COLORS = ['#78716C', '#A8A29E', '#D6D3D1', '#E7E5E4'];

  return (
    <div className="min-h-screen flex flex-col bg-white" onClick={handleCalculatorInteraction}>
      <PageSEO 
        title="Event Budget Calculator" 
        description="Plan your NYC event budget. Estimate venue costs, food & drink expenses for your next corporate or social event." 
        path="/calculator" 
        faqSchema={calculatorPageFaqSchema}
      />
      
      {/* Hero Section */}
      <section className="pb-4 px-5 sm:px-8" style={{ backgroundColor: '#F5F0EB' }}>
        <Navbar />
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="text-center"
          >
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.02em' }}>
              Event Budget Calculator
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-8 sm:py-10 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,350px),1fr] gap-8">
            {/* Left Column - Input Form */}
            <motion.div className="space-y-4" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-lg font-semibold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>Event Details</h2>
                <div className="flex items-center gap-2">
                  {hasInteracted && (
                    <motion.a href="/" className="sm:hidden inline-flex items-center gap-1.5 py-1 px-2.5 text-xs font-medium rounded-full bg-stone-800 text-white shadow-md" onClick={() => setHasInteracted(true)}>
                      Find Your Venue <ArrowRight className="w-3 h-3" />
                    </motion.a>
                  )}
                  {hasFormData(formData) && (
                    <motion.button onClick={handleClear} className="text-xs text-stone-600 hover:text-stone-800 bg-white px-3 py-1.5 rounded-xl border border-stone-200 hover:border-stone-300 shadow-sm" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      Clear All
                    </motion.button>
                  )}
                </div>
              </div>

              {/* Main Form */}
              <motion.div className="bg-[#FAFAF8] rounded-xl p-4 border border-stone-100 space-y-4" onClick={handleCalculatorInteraction}>
                {/* Event Basics */}
                <div>
                  <h3 className="text-sm font-medium text-stone-700 mb-3">Event Basics</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <FormControl fullWidth size="small">
                      <InputLabel>Guests</InputLabel>
                      <Select value={formData.numGuests} onChange={(e) => handleInputChange('numGuests', e.target.value)} label="Guests"
                        sx={{ backgroundColor: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D6D3D1', borderRadius: '0.75rem' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#A8A29E' } }}
                        MenuProps={{ PaperProps: { sx: { borderRadius: '0.75rem', maxWidth: '280px', '& .MuiMenuItem-root': { whiteSpace: 'normal' } } } }}
                        renderValue={(selected) => `${selected} guests`}>
                        {Object.values(GROUP_TIERS).map((tier) => (<MenuItem key={tier.name} value={tier.max}><div className="py-1"><span className="font-medium">{tier.max} guests</span></div></MenuItem>))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel>Duration</InputLabel>
                      <Select value={formData.duration} onChange={(e) => handleInputChange('duration', e.target.value)} label="Duration"
                        sx={{ backgroundColor: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D6D3D1', borderRadius: '0.75rem' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#A8A29E' } }}
                        MenuProps={{ PaperProps: { sx: { borderRadius: '0.75rem', maxWidth: '280px' } } }}>
                        {DURATION_OPTIONS.map((option) => (<MenuItem key={option} value={option}><span className="font-medium">{option}</span></MenuItem>))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel>Day</InputLabel>
                      <Select value={formData.eventDay} onChange={(e) => handleInputChange('eventDay', e.target.value)} label="Day"
                        sx={{ backgroundColor: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D6D3D1', borderRadius: '0.75rem' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#A8A29E' } }}
                        MenuProps={{ PaperProps: { sx: { borderRadius: '0.75rem', maxWidth: '280px' } } }}>
                        <MenuItem value="weekday"><span className="font-medium">Weekday</span></MenuItem>
                        <MenuItem value="weekend"><span className="font-medium">Weekend</span></MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel>Location</InputLabel>
                      <Select value={formData.location} onChange={(e) => handleInputChange('location', e.target.value)} label="Location"
                        sx={{ backgroundColor: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D6D3D1', borderRadius: '0.75rem' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#A8A29E' } }}
                        MenuProps={{ PaperProps: { sx: { borderRadius: '0.75rem', maxWidth: '280px' } } }}>
                        <MenuItem value="manhattan"><span className="font-medium">Manhattan</span></MenuItem>
                        <MenuItem value="brooklyn"><span className="font-medium">Brooklyn</span></MenuItem>
                        <MenuItem value="queens"><span className="font-medium">Queens</span></MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                </div>

                {/* Venue Style */}
                <VenueStyleSelector formData={formData} handleInputChange={handleInputChange} />

                {/* Services */}
                <div className="pt-4 border-t border-stone-200">
                  <div className="mb-3">
                    <ModernSwitch checked={formData.venueRentalOnly} onChange={(checked) => handleInputChange('venueRentalOnly', checked)} label="Venue Rental Only" />
                  </div>

                  {!formData.venueRentalOnly && (
                    <div className="mt-4 space-y-3">
                      <FormControl fullWidth size="small">
                        <InputLabel id="service-type-label">Service Type</InputLabel>
                        <Select labelId="service-type-label" value={formData.barPricingType} onChange={(e) => handleInputChange('barPricingType', e.target.value)} label="Service Type"
                          sx={{ backgroundColor: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D6D3D1', borderRadius: '0.75rem' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#A8A29E' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#78716C', borderWidth: '1px' } }}
                          MenuProps={{ PaperProps: { sx: { borderRadius: '0.75rem', maxWidth: '280px', '& .MuiMenuItem-root.Mui-selected': { backgroundColor: 'rgba(120, 113, 108, 0.08)' }, '& .MuiMenuItem-root:hover': { backgroundColor: 'rgba(120, 113, 108, 0.04)' } } } }}>
                          <MenuItem value="barMinimum">
                            <div className="py-1.5"><div className="flex items-center gap-2"><span>🍽️</span><span className="font-medium">Food & Beverage Minimum</span></div><div className="text-xs text-stone-500 mt-1 ml-7">Minimum spend requirement</div></div>
                          </MenuItem>
                          <MenuItem value="openBar">
                            <div className="py-1.5"><div className="flex items-center gap-2"><span>🥂</span><span className="font-medium">Open Bar</span></div><div className="text-xs text-stone-500 mt-1 ml-7">Per person pricing</div></div>
                          </MenuItem>
                        </Select>
                      </FormControl>

                      {formData.barPricingType === 'openBar' && (
                        <motion.div className="space-y-3 pl-3 border-l-2 border-stone-200 mt-3" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="bar-package-label">Bar Package</InputLabel>
                            <Select labelId="bar-package-label" value={formData.liquorQuality} onChange={(e) => handleInputChange('liquorQuality', e.target.value)} label="Bar Package"
                              sx={{ backgroundColor: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D6D3D1', borderRadius: '0.75rem' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#A8A29E' } }}>
                              <MenuItem value="limited"><div className="py-1"><span>🍷</span> <span className="font-medium">Limited (Beer & Wine)</span></div></MenuItem>
                              <MenuItem value="standard"><div className="py-1"><span>🥃</span> <span className="font-medium">Standard Bar</span></div></MenuItem>
                              <MenuItem value="premium"><div className="py-1"><span>🍸</span> <span className="font-medium">Premium Bar</span></div></MenuItem>
                            </Select>
                          </FormControl>

                          <FormControl fullWidth size="small">
                            <InputLabel id="food-options-label">Food Options</InputLabel>
                            <Select labelId="food-options-label" value={formData.foodTier} onChange={(e) => handleInputChange('foodTier', e.target.value)} label="Food Options"
                              sx={{ backgroundColor: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D6D3D1', borderRadius: '0.75rem' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#A8A29E' } }}>
                              <MenuItem value="none"><div className="py-1"><span>❌</span> <span className="font-medium">No Food</span></div></MenuItem>
                              <MenuItem value="appetizers"><div className="py-1"><span>🧀</span> <span className="font-medium">Appetizers</span></div></MenuItem>
                              <MenuItem value="buffet"><div className="py-1"><span>🍲</span> <span className="font-medium">Buffet</span></div></MenuItem>
                              <MenuItem value="plated"><div className="py-1"><span>🍽️</span> <span className="font-medium">Plated Dinner</span></div></MenuItem>
                            </Select>
                          </FormControl>
                        </motion.div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>

            {/* Right Column - Results */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
              <div className="bg-[#FAFAF8] rounded-xl p-6 border border-stone-100">
                {/* Total and Per Guest */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <motion.div className="bg-stone-800 rounded-xl p-5 text-white" style={{ background: 'linear-gradient(135deg, #292524, #1c1917)' }}>
                    <h2 className="text-sm font-medium text-stone-300 mb-1">Total Event Cost</h2>
                    <div className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>${roundTo25(costs.total || 0).toLocaleString()}</div>
                    <div className="flex flex-wrap items-center gap-1.5 text-xs text-stone-300 mt-3">
                      {formData.numGuests && <span className="bg-white/10 px-2 py-0.5 rounded-full">{formData.numGuests} guests</span>}
                      {formData.duration && <span className="bg-white/10 px-2 py-0.5 rounded-full">{formData.duration}</span>}
                      {formData.eventDay && <span className="bg-white/10 px-2 py-0.5 rounded-full">{formData.eventDay === 'weekend' ? 'Weekend' : 'Weekday'}</span>}
                    </div>
                    <p className="text-xs text-stone-400 mt-3 italic">Includes all taxes, fees, and gratuity</p>
                  </motion.div>

                  <div className="bg-white rounded-xl p-5 border border-stone-200">
                    <div className="text-center space-y-2">
                      <div className="text-sm font-medium text-stone-600">Cost per Guest</div>
                      <div className="text-2xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                        ${formData.numGuests ? (roundTo25(costs.total || 0) / parseInt(formData.numGuests)).toFixed(2) : '0.00'}
                      </div>
                      <div className="text-xs text-stone-500 italic">Includes all taxes, fees, and gratuity</div>
                      {formData.barPricingType === 'openBar' && formData.liquorQuality && (
                        <motion.div className="text-xs space-y-1 mt-2 py-2 px-3 bg-stone-50 rounded-lg" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                          <div className="text-stone-700 font-medium">Open Bar Rate</div>
                          <div className="text-stone-600">${(MULTIPLIERS.barPackage[formData.liquorQuality] * MULTIPLIERS.venueType[formData.venueType] * MULTIPLIERS.location[formData.location] * DURATION_SCALING[parseInt(formData.duration)] * GUEST_COUNT_SCALING[formData.numGuests]).toFixed(0)}/hour</div>
                        </motion.div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Chart and Breakdown */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-base font-medium text-gray-800 mb-3">Cost Breakdown</h3>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={generateChartData(costs)} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value">
                            {generateChartData(costs).map((entry, index) => (<Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} stroke="white" strokeWidth={2} />))}
                          </Pie>
                          <Tooltip formatter={(value, name, props) => [`$${roundTo25(value).toLocaleString()} (${props.payload.percentage}%)`, name]} contentStyle={{ backgroundColor: 'white', borderRadius: '0.75rem', border: '1px solid #E7E5E4', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                          <Legend verticalAlign="bottom" height={30} formatter={(value) => <span className="text-xs text-stone-600">{value}</span>} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-base font-medium text-gray-800 mb-3">Detailed Breakdown</h3>
                    <div className="space-y-1.5">
                      {[
                        formData.venueRentalOnly ? ['Venue Rental', roundTo25(costs.baseCost || 0)] : ['Food & Beverage', roundTo25((costs.barCost + costs.foodCost) || 0)],
                        ['Venue Fee', roundTo25(costs.adminFee || 0)],
                        !formData.venueRentalOnly ? ['Gratuity', roundTo25(costs.gratuity || 0)] : null,
                        ['Sales Tax', roundTo25(costs.tax || 0)]
                      ].filter(Boolean).map(([label, value]) => (
                        <motion.div key={label} className="flex justify-between items-center p-2 rounded-lg hover:bg-stone-100 transition-colors">
                          <span className="text-sm text-stone-600 font-medium">{label}</span>
                          <span className="text-sm text-stone-900 font-bold">${value.toLocaleString()}</span>
                        </motion.div>
                      ))}
                      <div className="pt-2 mt-2 border-t border-stone-200">
                        <motion.div className="flex justify-between items-center p-2 rounded-lg bg-stone-100">
                          <span className="text-sm font-medium text-stone-900">Grand Total</span>
                          <span className="text-sm font-bold text-stone-900">${roundTo25(costs.total || 0).toLocaleString()}</span>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </div>

                <SmartSuggestions formData={formData} costs={costs} handleInputChange={handleInputChange} />
              </div>
            </motion.div>
          </div>

          {/* CTA Section */}
          <motion.div className="mt-16 text-center py-12 px-6" style={{ backgroundColor: '#F5F0EB' }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Planning Your Event Shouldn't Feel Like Work</h2>
            <p className="text-stone-500 max-w-2xl mx-auto mb-8 text-base sm:text-lg">Let us match you with a great venue so you can focus on planning your event.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="/" className="inline-flex items-center justify-center px-8 py-3.5 text-base font-medium text-white bg-stone-900 hover:bg-stone-700 rounded-lg transition-colors shadow-md">
                Find Your Venue <ArrowRight className="ml-2 w-4 h-4" />
              </a>
              <a href="/contact" className="inline-flex items-center justify-center px-8 py-3.5 text-base font-medium text-stone-700 bg-white border border-stone-300 hover:bg-stone-50 rounded-lg transition-colors">
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* ───────── FAQ SECTION ───────── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAFAF8] border-t border-stone-100">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12 text-center"
          >
            <h2
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Calculator FAQ
            </h2>
            <p className="text-stone-500">
              Common questions about our NYC event cost estimates.
            </p>
          </motion.div>

          <div className="space-y-2">
            {calculatorPageFaqs.map((faq, idx) => (
              <FAQItem 
                key={idx} 
                faq={faq} 
                isOpen={openFaqIndex === idx} 
                toggle={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)} 
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
